import React, { useState } from 'react';
import { Plane, Heart, Package, Hotel, Home, LogIn, User, LogOut, Globe, ChevronDown, Inbox, BarChart3, CalendarDays } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../hooks/useAuth';
import AuthGateModal from './auth/AuthGateModal';

export default function Header({ activeSection, setActiveSection }) {
  const { t, language, setLanguage, languages } = useLanguage();
  const { user, signOut } = useAuth();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  // Local state for the auth modal — opening it shows email-signup,
  // Google AND Facebook (vs. the previous one-click Google flow).
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const navItems = [
    { id: 'home', icon: Home },
    { id: 'flights', icon: Plane },
    { id: 'hotels', icon: Hotel },
    { id: 'packages', icon: Package },
    { id: 'saved', icon: Heart },
  ];

  const handleSignOut = async () => {
    await signOut();
    setShowUserDropdown(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo — acts as a Home button. Bigger tap target on mobile for reliability. */}
          <div className="flex items-center">
            <button
              type="button"
              data-testid="home-logo"
              onClick={() => setActiveSection('home')}
              aria-label={t('header.home')}
              className="flex items-center space-x-2 cursor-pointer py-2 pr-2 -my-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                <Plane className="w-6 h-6 text-white" />
              </div>
              <div className="hidden sm:block text-left">
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  TravelAI
                </h1>
                <p className="text-xs text-gray-500">{t('header.smartAssistant')}</p>
              </div>
            </button>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeSection === item.id
                      ? 'bg-blue-50 text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{t(`header.${item.id}`)}</span>
                </button>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-3">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">{languages.find(l => l.code === language)?.flag}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              
              {showLanguageDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border py-1 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setLanguage(lang.code);
                        setShowLanguageDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 ${
                        language === lang.code ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  data-testid="header-user-menu-btn"
                  className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-blue-600 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <ChevronDown className="w-3 h-3" />
                </button>
                
                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1 z-50">
                    <div className="px-4 py-2 border-b">
                      <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setActiveSection('profile');
                        setShowUserDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <User className="w-4 h-4" />
                      <span>{t('header.profile')}</span>
                    </button>
                    {user.is_admin && (
                      <button
                        data-testid="header-admin-inbox-link"
                        onClick={() => {
                          setActiveSection('admin-inbox');
                          setShowUserDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-blue-700 hover:bg-blue-50 flex items-center space-x-2 border-t border-gray-100"
                      >
                        <Inbox className="w-4 h-4" />
                        <span>{t('admin.inbox.title')}</span>
                      </button>
                    )}
                    {user.is_admin && (
                      <button type="button" data-testid="header-admin-new-year-link"
                        onClick={() => { setActiveSection('admin-new-year'); setShowUserDropdown(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-2"
                      >
                        <CalendarDays className="w-4 h-4" />
                        <span>Нова година · публикации</span>
                      </button>
                    )}
                    {user.is_admin && (
                      <button
                        data-testid="header-admin-cache-link"
                        onClick={() => {
                          setActiveSection('admin-cache');
                          setShowUserDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-emerald-700 hover:bg-emerald-50 flex items-center space-x-2"
                      >
                        <BarChart3 className="w-4 h-4" />
                        <span>Cache analytics</span>
                      </button>
                    )}
                    <button
                      onClick={handleSignOut}
                      data-testid="sign-out-btn"
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{t('header.signOut')}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                data-testid="sign-in-btn"
                onClick={() => setAuthModalOpen(true)}
                className="flex items-center space-x-2 bg-midnight-900 hover:bg-midnight-800 text-ivory-50 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md min-h-[44px] ring-1 ring-gold-400/40 hover:ring-gold-400/70"
              >
                <LogIn className="w-4 h-4 text-gold-400" />
                <span>{t('header.signIn')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden pb-3 pt-2">
          <div className="flex space-x-1 overflow-x-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => setActiveSection(item.id)}
                  className={`flex flex-col items-center space-y-1 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    activeSection === item.id
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{t(`header.${item.id}`)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <AuthGateModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </header>
  );
}
